import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import Lightbox from '@/components/Lightbox';
import GallerySkeleton from '@/components/skeletons/GallerySkeleton';

// Static fallback images served from public/gallery/
const staticMasks = [
  '/gallery/slavie-praha.jpg',
  '/gallery/cesky-lev.jpg',
  '/gallery/kladno-retro.jpg',
  '/gallery/dansko.jpg',
  '/gallery/ceska-lipa.jpg',
  '/gallery/slavoj-zbraslav.jpg',
  '/gallery/verva-litvinov.jpg',
  '/gallery/vimperk.jpg',
  '/gallery/hst-a-tym.jpg',
  '/gallery/hst-trebic.jpg',
  '/gallery/sharks-brno.jpg',
  '/gallery/ttc.jpg',
  '/gallery/uh-hradiste.jpg',
  '/gallery/slovensky-led-2.jpg',
  '/gallery/slovensky-led.jpg',
  '/gallery/hst-od.jpg',
  '/gallery/medved.jpg',
  '/gallery/elias.jpg',
  '/gallery/predator.jpg',
  '/gallery/spartan.jpg',
  '/gallery/thor.jpg',
  '/gallery/trinec.jpg',
  
  '/gallery/panter.jpg',
  '/gallery/stir-maska.jpg',
];

const INITIAL_COUNT = 9;

interface GalleryItem {
  id: string;
  image_url: string;
  title: string;
  description: string | null;
}

// Cyan/Ice Blue accent colors matching the brand palette (HSL 195)
const glowColors = [
  'rgba(0, 200, 230, 0.6)',
  'rgba(0, 180, 215, 0.5)',
  'rgba(30, 210, 240, 0.6)',
  'rgba(0, 195, 225, 0.55)',
  'rgba(20, 205, 235, 0.6)',
  'rgba(0, 185, 220, 0.5)',
  'rgba(10, 200, 230, 0.55)',
  'rgba(0, 210, 240, 0.6)',
  'rgba(30, 195, 225, 0.5)',
  'rgba(15, 200, 230, 0.55)',
];

interface TiltCardProps {
  img: string;
  title: string;
  description: string;
  glowColor: string;
  onClick: () => void;
  index: number;
}

const TiltCard = ({ img, title, description, glowColor, onClick, index }: TiltCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { stiffness: 200, damping: 25 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), springConfig);

  const glowX = useSpring(useTransform(mouseX, [-0.5, 0.5], [0, 100]), springConfig);
  const glowY = useSpring(useTransform(mouseY, [-0.5, 0.5], [0, 100]), springConfig);

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      className="cursor-pointer"
      style={{ perspective: 1000 }}
      onClick={onClick}
    >
      <motion.div
        ref={ref}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative group"
      >
        {/* Glow halo behind card */}
        <motion.div
          className="absolute -inset-[2px] rounded-sm pointer-events-none z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: glowColor,
            filter: 'blur(18px)',
          }}
        />

        {/* Card surface */}
        <div
          className="relative z-10 overflow-hidden rounded-sm aspect-square"
          style={{ transformStyle: 'preserve-3d' }}
        >
          <img
            src={img}
            alt={title || `Mask ${index + 1}`}
            loading="lazy"
            onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
            className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-105"
          />

          {/* Dynamic spotlight shimmer following mouse */}
          {isHovered && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${glowX.get()}% ${glowY.get()}%, rgba(255,255,255,0.12) 0%, transparent 60%)`,
              }}
            />
          )}

          {/* Hover overlay with title */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <h3
              className="text-white font-bold text-lg drop-shadow-lg"
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {title}
            </h3>
            <p className="text-white/75 text-sm drop-shadow">{description}</p>
          </div>

          {/* Thin colored border on hover */}
          <div
            className="absolute inset-0 rounded-sm pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              boxShadow: `inset 0 0 0 1.5px ${glowColor}`,
            }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

const GalleryPreview = () => {
  const { t } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [dbItems, setDbItems] = useState<GalleryItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchGallery = async () => {
      const { data } = await supabase
        .from('gallery_items')
        .select('id, image_url, title, description')
        .eq('visible', true)
        .order('sort_order', { ascending: true });
      if (data && data.length > 0) {
        setDbItems(data);
      }
      setLoaded(true);
    };
    fetchGallery();
  }, []);

  const useDb = dbItems.length > 0;
  const images = useDb ? dbItems.map(item => item.image_url) : staticMasks;
  const titles = useDb ? dbItems.map(item => item.title) : t.masks.map(m => m.name);
  const descriptions = useDb ? dbItems.map(item => item.description || '') : t.masks.map(m => m.desc);

  const openLightbox = (i: number) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  if (!loaded) return <GallerySkeleton count={INITIAL_COUNT} />;

  return (
    <section id="gallery" className="py-20 md:py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="text-5xl md:text-7xl tracking-wider text-foreground"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t.gallery.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.gallery.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.slice(0, INITIAL_COUNT).map((img, i) => (
            <TiltCard
              key={useDb ? dbItems[i].id : `static-${i}`}
              img={img}
              title={titles[i]}
              description={descriptions[i]}
              glowColor={glowColors[i % glowColors.length]}
              onClick={() => openLightbox(i)}
              index={i}
            />
          ))}
          <AnimatePresence>
            {expanded && images.slice(INITIAL_COUNT).map((img, idx) => {
              const i = idx + INITIAL_COUNT;
              return (
                <motion.div
                  key={useDb ? dbItems[i].id : `static-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className=""
                >
                  <TiltCard
                    img={img}
                    title={titles[i]}
                    description={descriptions[i]}
                    glowColor={glowColors[i % glowColors.length]}
                    onClick={() => openLightbox(i)}
                    index={0}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {images.length > INITIAL_COUNT && (
          <div className="text-center mt-12">
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-8 py-3 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300 tracking-widest text-lg"
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {expanded
                ? t.gallery.showLess || 'Skrýt'
                : `${t.gallery.showAll || 'Zobrazit vše'} (${images.length})`}
            </button>
          </div>
        )}
      </div>

      <Lightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={() => setLightboxIndex((i) => (i + 1) % images.length)}
        onPrev={() => setLightboxIndex((i) => (i - 1 + images.length) % images.length)}
        titles={titles}
      />
    </section>
  );
};

export default GalleryPreview;
